// Browser client for the address-tools Supabase Edge Function. Reads/writes go
// through this function directly (fetch), NOT a Retool resource.
//
// NOTE ON CSP: this app is served with `connect-src 'self'`, which blocks
// browser requests to the Supabase origin. For these calls to succeed, an org
// admin must add https://tcsgeibgvwybyyavbukt.supabase.co to the connect-src
// directive under Settings > App Security > Content Security Policy.

import { freshToken } from '../hooks/useBackend'

const EDGE_URL = 'https://tcsgeibgvwybyyavbukt.supabase.co/functions/v1/address-tools'

// Public anon key — safe to embed in the client.
const ANON =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRjc2dlaWJndnd5Ynl5YXZidWt0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxNDQ5MjksImV4cCI6MjA5MjcyMDkyOX0.wTHzg_i6trFVpi8dT73ry-FJwtgXvG7TzJuxBmUsvrQ'

// The function refuses the anon key since 2 September 2026: saving here writes a
// driver's home coordinates as manually verified, so it needs to know a staff
// member is asking. The anon key still goes as `apikey` for the Supabase gateway,
// but Authorization carries the signed-in session, which is what it checks.
async function headers(): Promise<HeadersInit> {
  const token = await freshToken()
  if (!token) throw new Error('You are signed out. Sign in again, then retry the address.')
  return {
    'Content-Type': 'application/json',
    apikey: ANON,
    Authorization: 'Bearer ' + token,
  }
}

async function post<T>(body: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const res = await fetch(EDGE_URL, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    let detail = ''
    try {
      const j = (await res.json()) as { error?: string; message?: string }
      detail = j.error || j.message || ''
    } catch {
      // ignore parse failures
    }
    throw new Error(detail || `Request failed (${res.status})`)
  }
  return (await res.json()) as T
}

export type AddressSuggestion = { place_id: string; text: string }

export async function suggestAddresses(
  input: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  const data = await post<{ suggestions?: AddressSuggestion[] }>(
    { action: 'suggest', input },
    signal,
  )
  return data.suggestions ?? []
}

export type SaveAddressInput = {
  driver_id: string
  place_id?: string
  address_text?: string
  map_link?: string
}

export type SaveAddressResult = {
  driver_id: string
  geocode_status: string
  is_clean: boolean
}

export async function saveAddress(input: SaveAddressInput): Promise<SaveAddressResult> {
  return post<SaveAddressResult>({ action: 'save', ...input })
}
